import { isNil } from 'lodash';
import { Fund } from 'src/components/Exchange/types';
import {
  DELIVERY_DIRECT_DEPOSIT,
  DELIVERY_EXPRESS_DELIVERY,
  FundNote,
  NON_FIDUCIARY_PLAN,
  SystemCode,
  BUY_TRANSACTION,
  EXCHANGE_TRANSACTION,
  AGREEMENT_TYPES,
} from 'src/settings/settings';
import { UserFund } from 'src/types';
import {
  isBuy,
  isSell,
  isCollegeContribute,
  transactionType,
  isExchange,
} from '../../transactions-util';

const EXCHANGE_REQUIRED_FEATURES = [SystemCode.EXCHANGE_REQUIRED_FEATURE];

export const DISABLE_ON_MATCH = {};

/**
 * Set the SF variable
 */
let isSFRepEnabled = false;
export function setSFRepEnabled(sfRepEnabled: boolean) {
  isSFRepEnabled = sfRepEnabled;
}

/**
 * Check if the feature is being ignored for SF reps
 */
export function isSalesforceIgnoredFeature(serviceCode: number) {
  return false; // TODO: Re-enable Later: Object.values(SalesforceIgnoredSystemCodes).includes(serviceCode);
}

/**
 * Checks for existence of a found feature based on service code.
 */
export function checkFoundFeaturesByCode(
  fund: UserFund,
  serviceCode: number,
  isExchange = false
) {
  if (
    isNil(fund?.fundLock) ||
    (isSFRepEnabled && isSalesforceIgnoredFeature(serviceCode))
  ) {
    return false;
  }
  const {
    fundLock: { fundLocked, foundFeatures },
  } = fund;
  return Boolean(
    (fundLocked || isExchange) &&
      foundFeatures?.find(({ serviceID }) => serviceID === serviceCode)
  );
}

/**
 * Checks for existence of a missing feature based on service code.
 */
export function checkMissingFeaturesByCode(
  fund: Partial<UserFund>,
  serviceCode: number
) {
  if (
    isNil(fund?.fundLock) ||
    (isSFRepEnabled && isSalesforceIgnoredFeature(serviceCode))
  ) {
    return false;
  }
  const {
    fundLock: { fundLocked, missingRequiredFeatures },
  } = fund;
  return !!(
    fundLocked &&
    missingRequiredFeatures?.find(({ serviceID }) => serviceID === serviceCode)
  );
}

export function missingRedemptionFeature(fund: UserFund, deliveryMethod) {
  let redemption = '';
  if (checkMissingFeaturesByCode(fund, SystemCode.CHECK_REDEMPTION)) {
    return (redemption = 'all');
  }
  if (
    checkMissingFeaturesByCode(fund, SystemCode.ACH_REDEMPTION) &&
    deliveryMethod === DELIVERY_DIRECT_DEPOSIT &&
    (!checkMissingFeaturesByCode(fund, SystemCode.CHECK_REDEMPTION) ||
      !checkMissingFeaturesByCode(fund, SystemCode.WIRE_REDEMPTION))
  ) {
    return (redemption = 'ACH');
  }
  if (
    checkMissingFeaturesByCode(fund, SystemCode.WIRE_REDEMPTION) &&
    deliveryMethod === DELIVERY_EXPRESS_DELIVERY &&
    (!checkMissingFeaturesByCode(fund, SystemCode.ACH_REDEMPTION) ||
      !checkMissingFeaturesByCode(fund, SystemCode.CHECK_REDEMPTION))
  ) {
    return (redemption = 'Wire');
  }
  return redemption;
}

/**
 * Checks if fund has an available balance.
 *
 * @param {UserFund}
 * @returns {boolean}
 */
export function hasZeroBalance(fund) {
  return fund.balanceAsOf <= 0;
}

/**
 * Checks if fund is closed to new investors.
 *
 * @param {UserFund}
 * @returns {boolean}
 */
export function isClosedFund(fund) {
  return fund.openToNewInvestors === false;
}

/**
 * Checks if selected funding bank is amongst the list of
 * restricted banks for transaction.
 *
 * @param {UserFund}
 * @param {AgreementMapEx}
 * @returns {boolean}
 */
export function isTransactionRestrictedForFundingBank(
  fund,
  selectedBankAgreementMap
) {
  const { associableLookup, associatedLookup, restrictedLookup } =
    selectedBankAgreementMap;

  const isRestricted =
    restrictedLookup[fund.uniqueKey] ||
    (fund.newFund &&
      fund.modelAgreementUniqueIDs.every(
        (uid) => !associableLookup[uid] && !associatedLookup[uid]
      ));

  return isRestricted;
}

export function isTransactionPendingStatusAgreements(
  fund,
  selectedBankAgreementMap
) {
  const { pendingStatusAgreements } = selectedBankAgreementMap;
  const pendingAgreements =
    pendingStatusAgreements?.some((key) => key === fund.uniqueKey) || false;

  return pendingAgreements;
}

export function checkBankServiceFeature(fund, deliveryMethod, selectedBank) {
  const serviceFeatures = selectedBank
    ? selectedBank.serviceFeatures
      ? selectedBank.serviceFeatures
      : {}
    : {};
  const bankAgreements = serviceFeatures.bankAgreementServiceFeatures
    ? serviceFeatures.bankAgreementServiceFeatures
    : null;
  if (bankAgreements) {
    if (bankAgreements.length > 0) {
      return bankAgreements.some((agreement) => {
        if (agreement.agreement === fund.uniqueKey) {
          const serviceIDs = agreement.activeServiceFeatures.map(
            (serviceFeature) => {
              return serviceFeature.serviceFeature.serviceID;
            }
          );
          return (
            !serviceIDs.includes(SystemCode.WIRE_REDEMPTION) &&
            !serviceIDs.includes(SystemCode.ACH_REDEMPTION)
          );
        }
        return false;
      });
    } else {
      return true;
    }
  }
  return false;
}

export function checkACHBankServiceFeature(fund, deliveryMethod, selectedBank) {
  const serviceFeatures = selectedBank
    ? selectedBank.serviceFeatures
      ? selectedBank.serviceFeatures
      : {}
    : {};
  const bankAgreements = serviceFeatures.bankAgreementServiceFeatures
    ? serviceFeatures.bankAgreementServiceFeatures
    : [];
  if (deliveryMethod === DELIVERY_DIRECT_DEPOSIT) {
    if (bankAgreements.length > 0) {
      return bankAgreements.some((agreement) => {
        if (agreement.agreement === fund.uniqueKey) {
          const serviceIDs = agreement.activeServiceFeatures.map(
            (serviceFeature) => {
              return serviceFeature.serviceFeature.serviceID;
            }
          );
          return (
            !serviceIDs.includes(SystemCode.ACH_REDEMPTION) &&
            serviceIDs.includes(SystemCode.WIRE_REDEMPTION)
          );
        }
        return false;
      });
    }
  }
  return false;
}

export function checkWireBankServiceFeature(
  fund,
  deliveryMethod,
  selectedBank
) {
  const serviceFeatures = selectedBank
    ? selectedBank.serviceFeatures
      ? selectedBank.serviceFeatures
      : {}
    : {};
  const bankAgreements = serviceFeatures.bankAgreementServiceFeatures
    ? serviceFeatures.bankAgreementServiceFeatures
    : [];
  if (deliveryMethod === DELIVERY_EXPRESS_DELIVERY) {
    if (bankAgreements.length > 0) {
      return bankAgreements.some((agreement) => {
        if (agreement.agreement === fund.uniqueKey) {
          const serviceIDs = agreement.activeServiceFeatures.map(
            (serviceFeature) => {
              return serviceFeature.serviceFeature.serviceID;
            }
          );
          return (
            serviceIDs.includes(SystemCode.ACH_REDEMPTION) &&
            !serviceIDs.includes(SystemCode.WIRE_REDEMPTION)
          );
        }
        return false;
      });
    }
  }
  return false;
}

export function updateFundNote(
  fund: Fund | UserFund,
  condition?: boolean,
  note?: FundNote,
  disableOnMatch?
) {
  if (!condition) {
    return;
  }
  if (note) {
    fund.notes[note] = true;
  }

  if (disableOnMatch) {
    fund.disabled = true;
    delete fund.selected;
  }
}

export function isTransactionAssociatedForFundingBank(
  fund,
  selectedBankAgreementMap
) {
  const { associatedLookup } = selectedBankAgreementMap;
  return associatedLookup[fund.uniqueKey];
}
/**
 * Checks to make sure that required service features are present (not included in missingRequiredFeatures property)
 * @param {*} fund
 */
export function hasRequiredServiceFeatures(fund) {
  const isBuyOrSellOrContribute = isBuy() || isSell() || isCollegeContribute();
  if (isNil(fund.fundLock) || isBuyOrSellOrContribute) {
    return true;
  }
  const {
    fundLock: { missingRequiredFeatures },
  } = fund;
  return missingRequiredFeatures &&
    missingRequiredFeatures.find((feature) =>
      EXCHANGE_REQUIRED_FEATURES.includes(feature.serviceID)
    )
    ? false
    : true;
}

/**
 * Checks to see if from and to funds are consistent with respect to fiduciary or non-fiduciary funds
 * @param {*} firstFund
 * @param {*} fund
 * @returns {boolean}
 */
export function disableFiduciaryOrNonFiduciary(firstFund, fund) {
  const { agreementType: agreementTypeFrom } = firstFund || {};
  const { agreementType: agreementTypeTo } = fund || {};

  return (
    (agreementTypeFrom === NON_FIDUCIARY_PLAN &&
      agreementTypeTo !== NON_FIDUCIARY_PLAN) ||
    (agreementTypeFrom !== NON_FIDUCIARY_PLAN &&
      agreementTypeTo === NON_FIDUCIARY_PLAN)
  );
}

export function isRolloverOrTraditionalIRA(selectedFund) {
  return (
    selectedFund &&
    (selectedFund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA ||
      selectedFund.agreementType === AGREEMENT_TYPES.ROLLOVER_IRA)
  );
}

/**
 * Checks if fund is a newly added iClass fund
 *
 * @param {UserFund}
 * @returns {boolean}
 */
export function isNewIClassFund(fund) {
  return fund.newFund && fund.iClass;
}

export function isNewOrReopenIClassFund(fund) {
  return fund.iClass && (fund.newFund || fund?.balanceAsOf === 0);
}

export function checkMFBuyOrExchangeFlow() {
  const transactioType = transactionType();
  const isMFBuyOrExchangeFlow = [
    BUY_TRANSACTION,
    EXCHANGE_TRANSACTION,
  ].includes(transactioType);

  if (isMFBuyOrExchangeFlow) {
    return true;
  } else {
    return false;
  }
}

export function getHouseHoldInformation(selectedRegistration) {
  const { agreementKeyHouseholds, agreementType } = selectedRegistration;
  const householdId = agreementKeyHouseholds
    ? agreementKeyHouseholds[0]?.household?.householdBalanceTierStatus
        ?.householdAssetTier?.id
    : null;
  let getSCSTier;
  const isIRA = agreementType?.toUpperCase().includes('IRA');
  const isBuyFlow = isBuy();
  const isExchangeFlow = isExchange();
  if (isBuyFlow) {
    getSCSTier =
      !isIRA && (householdId === 4 || householdId === 5)
        ? 'iClass'
        : (isIRA && (householdId === 4 || householdId === 5)) ||
          householdId === 3
        ? 'closed'
        : null;
  }

  if (isExchangeFlow) {
    getSCSTier = getSCSTier =
      householdId === 4 || householdId === 5
        ? 'iClass'
        : householdId === 3
        ? 'closed'
        : null;
  }
  return getSCSTier;
}
